import { useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Badge,
  Select
} from '@etherisc/ui-kit';
import { EditableCell } from './EditableCell';
import type { 
  PerilWeighting, 
  CoverageLevelLimits, 
  EnforcementType, 
  MinMax,
  StrikeExhaust
} from '../../types/product';
import { convertPerilIdsToNames } from '../../config/products';

interface NewEnforcementTableProps {
  title: string;
  description: string;
  availablePerils: string[];
  readOnly?: boolean;
  
  // For Peril Weighting
  perilWeighting?: PerilWeighting;
  onPerilWeightingChange?: (weights: PerilWeighting) => void;
  
  // For Coverage Level Limits  
  coverageLevelLimits?: CoverageLevelLimits;
  onCoverageLevelLimitsChange?: (limits: CoverageLevelLimits) => void;

  globalLimits?: Record<string, StrikeExhaust>;
}

export function NewEnforcementTable({
  title,
  description,
  availablePerils,
  readOnly = false,
  perilWeighting,
  onPerilWeightingChange,
  coverageLevelLimits,
  onCoverageLevelLimitsChange,
  globalLimits
}: NewEnforcementTableProps) {
  
  const editableCellRefs = useRef<Record<string, HTMLElement | null>>({});
  
  // Helper function to get display name for peril ID
  const getPerilDisplayName = (perilId: string): string => {
    const displayNames = convertPerilIdsToNames([perilId]);
    return displayNames[0] || perilId;
  };
  
  // Determine which mode we're in
  const isPerilWeighting = !!perilWeighting && !!onPerilWeightingChange;
  const isCoverageLimits = !!coverageLevelLimits && !!onCoverageLevelLimitsChange;
  
  if (!isPerilWeighting && !isCoverageLimits) {
    throw new Error('NewEnforcementTable: Must provide either perilWeighting or coverageLevelLimits props');
  }

  // Get current enforcement and data
  const currentEnforcement: EnforcementType = isPerilWeighting 
    ? perilWeighting!.enforcement 
    : coverageLevelLimits!.enforcement;
    
  // Initialize weights/limits with enforcement logic applied
  const initializeWeights = (weights: Record<string, MinMax>, enforcement: EnforcementType): Record<string, MinMax> => {
    const initialized: Record<string, MinMax> = {};
    availablePerils.forEach(peril => {
      const weight = weights[peril] || { min: 5, max: 5 };
      if (enforcement === 'default' || enforcement === 'fixed') {
        // For default/fixed, use the min value if it exists, otherwise default to 5
        const value = weight.min !== undefined ? weight.min : 5;
        initialized[peril] = { min: value, max: value };
      } else {
        initialized[peril] = weight;
      }
    });
    return initialized;
  };

  const initializeLimits = (limits: Record<string, StrikeExhaust>, enforcement: EnforcementType): Record<string, StrikeExhaust> => {
    const initialized: Record<string, StrikeExhaust> = {};
    availablePerils.forEach(peril => {
      const limit = limits[peril] || { strike: { min: 5, max: 5 }, exhaust: { min: 5, max: 5 } };
      if (enforcement === 'default' || enforcement === 'fixed') {
        // For default/fixed, use the min values if they exist, otherwise default to 5
        const strikeValue = limit.strike.min !== undefined ? limit.strike.min : 5;
        const exhaustValue = limit.exhaust.min !== undefined ? limit.exhaust.min : 5;
        initialized[peril] = {
          strike: { min: strikeValue, max: strikeValue },
          exhaust: { min: exhaustValue, max: exhaustValue }
        };
      } else {
        initialized[peril] = limit;
      }
    });
    return initialized;
  };

  const currentWeights = isPerilWeighting ? initializeWeights(perilWeighting!.weights, currentEnforcement) : {};
  const currentLimits = isCoverageLimits ? initializeLimits(coverageLevelLimits!.limits, currentEnforcement) : {};

  // Initialize data on component mount for default/fixed enforcement
  useEffect(() => {
    if (readOnly) return;
    
    if (isPerilWeighting && onPerilWeightingChange && (currentEnforcement === 'default' || currentEnforcement === 'fixed')) {
      const initializedWeights = initializeWeights(perilWeighting!.weights, currentEnforcement);
      const hasChanges = availablePerils.some(peril => {
        const original = perilWeighting!.weights[peril];
        const initialized = initializedWeights[peril];
        return original && (original.max !== initialized.max);
      });
      
      if (hasChanges) {
        onPerilWeightingChange({
          enforcement: currentEnforcement,
          weights: initializedWeights
        });
      }
    } else if (isCoverageLimits && onCoverageLevelLimitsChange && (currentEnforcement === 'default' || currentEnforcement === 'fixed')) {
      const initializedLimits = initializeLimits(coverageLevelLimits!.limits, currentEnforcement);
      const hasChanges = availablePerils.some(peril => {
        const original = coverageLevelLimits!.limits[peril];
        const initialized = initializedLimits[peril];
        return original && (
          original.strike.max !== initialized.strike.max ||
          original.exhaust.max !== initialized.exhaust.max
        );
      });
      
      if (hasChanges) {
        onCoverageLevelLimitsChange({
          enforcement: currentEnforcement,
          limits: initializedLimits
        });
      }
    }
  }, [availablePerils.length, currentEnforcement, isPerilWeighting, isCoverageLimits]);

  const handleEnforcementChange = (enforcement: EnforcementType) => {
    if (readOnly) return;
    
    if (isPerilWeighting && onPerilWeightingChange) {
      // Initialize weights for all perils if switching enforcement types
      const initializedWeights: Record<string, MinMax> = {};
      availablePerils.forEach(peril => {
        const currentWeight = currentWeights[peril] || { min: 5, max: 5 };
        if (enforcement === 'default' || enforcement === 'fixed') {
          // For default/fixed enforcement, ensure max equals min
          const value = currentWeight.min !== undefined ? currentWeight.min : 5;
          initializedWeights[peril] = { min: value, max: value };
        } else {
          initializedWeights[peril] = currentWeight;
        }
      });
      
      onPerilWeightingChange({
        enforcement,
        weights: initializedWeights
      });
    } else if (isCoverageLimits && onCoverageLevelLimitsChange) {
      // Initialize limits for all perils if switching enforcement types
      const initializedLimits: Record<string, { strike: { min: number; max: number }; exhaust: { min: number; max: number } }> = {};
      availablePerils.forEach(peril => {
        const currentLimit = currentLimits[peril] || { 
          strike: { min: 5, max: 5 }, 
          exhaust: { min: 5, max: 5 } 
        };
        
        if (enforcement === 'default' || enforcement === 'fixed') {
          // For default/fixed enforcement, ensure max equals min
          const strikeValue = currentLimit.strike.min !== undefined ? currentLimit.strike.min : 5;
          const exhaustValue = currentLimit.exhaust.min !== undefined ? currentLimit.exhaust.min : 5;
          initializedLimits[peril] = {
            strike: { min: strikeValue, max: strikeValue },
            exhaust: { min: exhaustValue, max: exhaustValue }
          };
        } else {
          initializedLimits[peril] = currentLimit;
        }
      });
      
      onCoverageLevelLimitsChange({
        enforcement,
        limits: initializedLimits
      });
    }
  };

  const handleWeightChange = (peril: string, field: 'min' | 'max', value: number) => {
    if (readOnly || !isPerilWeighting || !onPerilWeightingChange) return;
    
    const currentWeight = currentWeights[peril] || { min: 5, max: 5 };
    let newWeight = { ...currentWeight, [field]: value }; // eslint-disable-line prefer-const
    
    // Apply enforcement logic
    if (currentEnforcement === 'default' || currentEnforcement === 'fixed') {
      // For default/fixed enforcement, max should always equal min
      if (field === 'min') {
        newWeight.max = value;
      } else {
        newWeight.min = value;
      }
    } else if (currentEnforcement === 'range') {
      // For range enforcement, validate min <= max
      if (field === 'min' && value > newWeight.max) {
        newWeight.max = value;
      } else if (field === 'max' && value < newWeight.min) {
        newWeight.min = value;
      }
    }
    
    const newWeights = {
      ...currentWeights,
      [peril]: newWeight
    };
    
    onPerilWeightingChange({
      enforcement: currentEnforcement,
      weights: newWeights
    });
  };

  const handleLimitsChange = (peril: string, field: 'strike' | 'exhaust', subField: 'min' | 'max', value: number) => {
    if (readOnly || !isCoverageLimits || !onCoverageLevelLimitsChange) return;
    
    const currentPerilLimits = currentLimits[peril] || {
      strike: { min: 5, max: 5 },
      exhaust: { min: 5, max: 5 }
    };
    
    const newFieldValue = { ...currentPerilLimits[field], [subField]: value };
    
    if (currentEnforcement === 'default' || currentEnforcement === 'fixed') {
      if (subField === 'min') {
        newFieldValue.max = value;
      } else {
        newFieldValue.min = value;
      }
    }
    
    const newLimits = {
      ...currentLimits,
      [peril]: {
        ...currentPerilLimits,
        [field]: newFieldValue
      }
    };
    
    onCoverageLevelLimitsChange({
      enforcement: currentEnforcement,
      limits: newLimits
    });
  };

  const handleCellNavigation = useCallback((rowIndex: number, colIndex: number, direction: 'up' | 'down' | 'left' | 'right') => {
    const totalCols = availablePerils.length; // Both modes use per-peril columns
    const totalRows = isPerilWeighting ? 2 : 4; // Peril weighting: min/max, Coverage limits: strike min/max, exhaust min/max
    
    let newRow = rowIndex;
    let newCol = colIndex;
    
    switch (direction) {
      case 'up':
        newRow = Math.max(0, rowIndex - 1);
        break;
      case 'down':
        newRow = Math.min(totalRows - 1, rowIndex + 1);
        break;
      case 'left':
        newCol = Math.max(0, colIndex - 1);
        break;
      case 'right':
        newCol = Math.min(totalCols - 1, colIndex + 1);
        break;
    }
    
    const newCellKey = `${newRow}-${newCol}`;
    const cellRef = editableCellRefs.current[newCellKey];
    if (cellRef) {
      cellRef.focus();
    }
  }, [availablePerils.length, isPerilWeighting]);

  const cellErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!isCoverageLimits || currentEnforcement !== 'range') return errors;

    availablePerils.forEach(peril => {
      const perilLimits = currentLimits[peril];
      if (!perilLimits) return;

      const global = globalLimits?.[peril];

      for (const type of ['strike', 'exhaust'] as const) {
        const { min, max } = perilLimits[type];

        if (max < min) {
          errors[`${peril}-${type}-max`] = 'Maximum must be greater than or equal to minimum';
        }

        if (global) {
          const gMin = global[type].min;
          const gMax = global[type].max;
          if (min < gMin || min > gMax) {
            errors[`${peril}-${type}-min`] = `Value must be between ${gMin} and ${gMax}`;
          }
          if (max < gMin || max > gMax) {
            errors[`${peril}-${type}-max`] =
              errors[`${peril}-${type}-max`]
                ? `${errors[`${peril}-${type}-max`]}; Value must be between ${gMin} and ${gMax}`
                : `Value must be between ${gMin} and ${gMax}`;
          }
        }
      }
    });

    return errors;
  }, [isCoverageLimits, currentEnforcement, currentLimits, globalLimits, availablePerils]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          {title}
          <Badge variant="outline" className="text-xs">
            {currentEnforcement.toUpperCase()}
          </Badge>
        </CardTitle>
        <p className="text-sm text-gray-600 mt-2">{description}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Enforcement Type Selection */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Enforcement Type:</label>
          <Select
            value={currentEnforcement}
            onValueChange={(value) => handleEnforcementChange(value as EnforcementType)}
            disabled={readOnly}
            options={[
              { value: 'default', label: 'Default' },
              { value: 'fixed', label: 'Fixed' },
              { value: 'range', label: 'Range' }
            ]}
            className="w-40"
          />
        </div>

        {/* Peril Weighting Table */}
        {isPerilWeighting && availablePerils.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Range</TableHead>
                  {availablePerils.map(peril => (
                    <TableHead key={peril} className="text-center min-w-24">
                      {getPerilDisplayName(peril)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {['min', 'max'].map((rangeType, rowIndex) => (
                  <TableRow key={rangeType}>
                    <TableCell className="font-medium capitalize">{rangeType}</TableCell>
                    {availablePerils.map((peril, colIndex) => {
                      const cellKey = `${rowIndex}-${colIndex}`;
                      const currentWeight = currentWeights[peril] || { min: 5, max: 5 };
                      return (
                        <TableCell key={`${peril}-${rangeType}`} className="text-center p-2">
                          <div className="flex justify-center">
                            <EditableCell
                              ref={(el) => {
                                editableCellRefs.current[cellKey] = el;
                              }}
                              value={currentWeight[rangeType as 'min' | 'max']}
                              onChange={(newValue) => 
                                handleWeightChange(peril, rangeType as 'min' | 'max', newValue)
                              }
                              type="number"
                              min={0}
                              max={100}
                              step={0.1}
                              suffix="%"
                              className="w-20 text-center"
                              disabled={readOnly}
                              onNavigate={(direction) => {
                                if (direction === 'next') {
                                  handleCellNavigation(rowIndex, colIndex, 'right');
                                } else {
                                  handleCellNavigation(rowIndex, colIndex, 'left');
                                }
                              }}
                            />
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Coverage Level Limits Table - Per-Peril */}
        {isCoverageLimits && availablePerils.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Limit Type</TableHead>
                  {availablePerils.map(peril => (
                    <TableHead key={peril} className="text-center min-w-24">
                      {getPerilDisplayName(peril)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { type: 'strike', field: 'min', label: 'Strike Min' },
                  { type: 'strike', field: 'max', label: 'Strike Max' },
                  { type: 'exhaust', field: 'min', label: 'Exhaust Min' },
                  { type: 'exhaust', field: 'max', label: 'Exhaust Max' }
                ].map((row, rowIndex) => (
                  <TableRow key={`${row.type}-${row.field}`}>
                    <TableCell className="font-medium">{row.label}</TableCell>
                    {availablePerils.map((peril, colIndex) => {
                      const cellKey = `${rowIndex}-${colIndex}`;
                      const currentValue = currentLimits[peril]?.[row.type as 'strike' | 'exhaust']?.[row.field as 'min' | 'max'] || 5;
                      const errorKey = `${peril}-${row.type}-${row.field}`;
                      const error = cellErrors[errorKey];
                      
                      return (
                        <TableCell key={`${peril}-${row.type}-${row.field}`} className="text-center p-2">
                          <div className="flex flex-col items-center">
                            <EditableCell
                              ref={(el) => {
                                editableCellRefs.current[cellKey] = el;
                              }}
                              value={currentValue}
                              onChange={(newValue) => 
                                handleLimitsChange(peril, row.type as 'strike' | 'exhaust', row.field as 'min' | 'max', newValue)
                              }
                              type="number"
                              min={0}
                              max={1000}
                              step={1}
                              suffix=" yrs"
                              className={`w-20 text-center ${error ? 'border-red-500' : ''}`}
                              disabled={readOnly}
                              onNavigate={(direction) => {
                                if (direction === 'next') {
                                  handleCellNavigation(rowIndex, colIndex, 'right');
                                } else {
                                  handleCellNavigation(rowIndex, colIndex, 'left');
                                }
                              }}
                            />
                            {error && (
                              <span className="text-xs text-red-600 mt-1 max-w-24 leading-tight">{error}</span>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* No Perils Message */}
        {availablePerils.length === 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-blue-700 text-sm">
              <strong>Note:</strong> No perils are configured. Please configure perils in the Coverage Configuration section first to see {isPerilWeighting ? 'weighting' : 'limit'} columns.
            </div>
          </div>
        )}

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-blue-700 text-sm space-y-2">
            <p><strong>Enforcement Types:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Default:</strong> Use system default values (min = max)</li>
              <li><strong>Fixed:</strong> Require exact values (min = max)</li>
              <li><strong>Range:</strong> Allow values within specified range (min ≤ value ≤ max)</li>
            </ul>
            {isPerilWeighting && (
              <p className="mt-2"><strong>Weights:</strong> Percentage values that determine the relative importance of each peril in premium calculations.</p>
            )}
            {isCoverageLimits && (
              <p className="mt-2"><strong>Coverage Limits:</strong> Strike and exhaust values define the coverage trigger and limit thresholds in return period years.</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
